import { useEffect, useMemo, useState } from "react";
import {
	buildAdminTextStyle,
	findAdminTextEntry,
	formatAdminTextValue,
	getAdminTextValue,
	getPublishedAdminTextRegistry,
	hasAdminRegistryKey,
	queueAdminRuntimeDetection,
	type AdminTextEntry,
	type AdminTextRegistry,
} from "../../services/AdminTextService";

export default function useAdminText() {
	const [registry, setRegistry] = useState<AdminTextRegistry | null>(null);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;
		setLoading(true);
		setLoadError(null);
		getPublishedAdminTextRegistry()
			.then((data) => {
				if (!mounted) return;
				setRegistry(data);
			})
			.catch(() => {
				if (!mounted) return;
				setLoadError("admin_text_load_failed");
			})
			.finally(() => {
				if (!mounted) return;
				setLoading(false);
			});

		return () => {
			mounted = false;
		};
	}, []);

	const palette = registry?.palette || [];

	return useMemo(
		() => ({
			registry,
			loading,
			loadError,
			getEntry: (key: string, fallback: string): AdminTextEntry =>
				findAdminTextEntry(registry, key, fallback),
			getText: (
				key: string,
				fallback: string,
				variables?: Record<string, string | number | null | undefined>,
			): string => {
				if (registry && !hasAdminRegistryKey(registry, key)) {
					queueAdminRuntimeDetection({
						key,
						fallback,
						group: "admin",
					});
				}
				return getAdminTextValue(findAdminTextEntry(registry, key, fallback), variables);
			},
			formatText: (
				value: string,
				variables?: Record<string, string | number | null | undefined>,
			): string => formatAdminTextValue(value, variables),
			getStyle: (key: string, fallback: string) =>
				buildAdminTextStyle(findAdminTextEntry(registry, key, fallback), palette),
		}),
		[loadError, loading, palette, registry],
	);
}
